/**
 * DomainSidebar — 左侧"收件域"选择器（docs/ui_page_head_detail 主从布局左栏）
 * 功能：搜索过滤 + 选中高亮 + 分页 + 新建/编辑/删除入口。
 * 由 DomainCenterPage 持有 domains 数据与选中态，本组件只负责渲染与回调。
 */
import { useMemo, useState } from 'react'
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Card,
  Badge,
  Spinner,
} from '@fluentui/react-components'
import {
  AddRegular,
  SearchRegular,
  DismissRegular,
  ArrowClockwiseRegular,
  EditRegular,
  DeleteRegular,
} from '@fluentui/react-icons'
import type { Domain } from '@/api/types'

export const DOMAIN_PAGE_SIZE = 12

const useStyles = makeStyles({
  left: {
    flex: '0 0 320px',
    minWidth: '280px',
    display: 'flex',
    flexDirection: 'column',
    padding: '12px',
    height: '100%',
    boxSizing: 'border-box',
  },
  leftHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingBottom: '10px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: '8px',
  },
  leftTitle: { fontWeight: tokens.fontWeightSemibold, flex: 1 },
  filterRow: { display: 'flex', gap: '8px', marginBottom: '8px', flexShrink: 0 },
  listWrap: { flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' },
  list: { display: 'flex', flexDirection: 'column', gap: '2px' },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '8px 10px',
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    border: '1px solid transparent',
    ':hover': { background: tokens.colorNeutralBackground1Hover },
  },
  itemActive: {
    background: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke2}`,
    ':hover': { background: tokens.colorBrandBackground2 },
  },
  itemBody: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, flex: 1 },
  itemTop: { display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flexWrap: 'wrap' },
  itemName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  itemSub: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemActions: { display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 },
  emptyList: {
    padding: '24px 8px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  loading: { padding: '40px', textAlign: 'center' },
  pager: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0,
    paddingTop: '8px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: '8px',
  },
  pageInfo: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    flexShrink: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  pageBtn: { minWidth: '24px', padding: '0 2px' },
  countText: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    padding: '6px 2px 2px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: '6px',
    flexShrink: 0,
  },
})

interface DomainSidebarProps {
  domains: Domain[]
  loading: boolean
  selectedId: number | null
  onSelect: (id: number) => void
  onNew: () => void
  onEdit: (d: Domain) => void
  onDelete: (d: Domain) => void
  onRefresh: () => void
  /** 每项子标题，例如 "3 个邮箱"；缺省显示描述 */
  countLabel?: (d: Domain) => string
  emptyHint?: string
}

export default function DomainSidebar(props: DomainSidebarProps) {
  const {
    domains,
    loading,
    selectedId,
    onSelect,
    onNew,
    onEdit,
    onDelete,
    onRefresh,
    countLabel,
    emptyHint,
  } = props
  const styles = useStyles()

  const [keyword, setKeyword] = useState('')
  const [page, setPage] = useState(1)

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase()
    if (!q) return domains
    return domains.filter((d) => d.name.toLowerCase().includes(q))
  }, [domains, keyword])

  const totalPages = Math.max(1, Math.ceil(filtered.length / DOMAIN_PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageItems = filtered.slice((safePage - 1) * DOMAIN_PAGE_SIZE, safePage * DOMAIN_PAGE_SIZE)

  return (
    <Card className={styles.left}>
      <div className={styles.leftHeader}>
        <Text className={styles.leftTitle}>收件域</Text>
        <Button appearance="subtle" size="small" icon={<AddRegular />} title="新建域" onClick={onNew} />
        <Button
          appearance="subtle"
          size="small"
          icon={<ArrowClockwiseRegular />}
          title="刷新"
          onClick={onRefresh}
        />
      </div>

      <div className={styles.filterRow}>
        <Input
          size="small"
          placeholder="过滤：域名"
          value={keyword}
          contentBefore={<SearchRegular />}
          contentAfter={
            keyword ? (
              <Button
                appearance="transparent"
                size="small"
                icon={<DismissRegular />}
                onClick={() => setKeyword('')}
              />
            ) : undefined
          }
          onChange={(_, d) => setKeyword(d.value)}
        />
      </div>

      <div className={styles.listWrap}>
        {loading ? (
          <div className={styles.loading}><Spinner size="small" label="加载中..." /></div>
        ) : filtered.length === 0 ? (
          <div className={styles.emptyList}>{keyword ? '没有匹配的域名' : emptyHint ?? '暂无收件域'}</div>
        ) : (
          <div className={styles.list}>
            {pageItems.map((d) => {
              const active = d.id === selectedId
              return (
                <div
                  key={d.id}
                  className={active ? `${styles.item} ${styles.itemActive}` : styles.item}
                  onClick={() => {
                    onSelect(d.id)
                    setPage(safePage)
                  }}
                >
                  <div className={styles.itemBody}>
                    <div className={styles.itemTop}>
                      <span className={styles.itemName}>{d.name}</span>
                      {!d.active && <Badge appearance="outline" color="subtle" size="small">停用</Badge>}
                    </div>
                    <div className={styles.itemSub}>
                      {countLabel ? countLabel(d) : (d.description || '收件域')}
                    </div>
                  </div>
                  <div className={styles.itemActions} onClick={(e) => e.stopPropagation()}>
                    <Button appearance="subtle" size="small" icon={<EditRegular />} title="编辑" onClick={() => onEdit(d)} />
                    <Button appearance="subtle" size="small" icon={<DeleteRegular />} title="删除" onClick={() => onDelete(d)} />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className={styles.pager}>
        <span className={styles.pageInfo}>
          {filtered.length === 0
            ? '0 条'
            : `${(safePage - 1) * DOMAIN_PAGE_SIZE + 1}-${Math.min(safePage * DOMAIN_PAGE_SIZE, filtered.length)} / ${filtered.length}`}
        </span>
        <Button size="small" appearance="subtle" className={styles.pageBtn} onClick={() => setPage(safePage - 1)} disabled={safePage <= 1} aria-label="上一页">‹</Button>
        <Text size={200} style={{ whiteSpace: 'nowrap' }}>{safePage} / {totalPages}</Text>
        <Button size="small" appearance="subtle" className={styles.pageBtn} onClick={() => setPage(safePage + 1)} disabled={safePage >= totalPages} aria-label="下一页">›</Button>
      </div>
      <div className={styles.countText}>共 {filtered.length} 个收件域</div>
    </Card>
  )
}